import { useCallback, useMemo, useState } from 'react';
import { COMMAND_CENTER_BASE_URL, ENDPOINTS } from 'features/command-center/constants';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import isEqual from 'lodash/isEqual';
import { useUserContext } from 'providers/UserProvider';
import queryString from 'query-string';
import { filtersToParams } from 'selectors/app';
import { SECONDARY_API_CLIENT } from 'services/API';
import { hasSingleRole, userHasRoles, userHasSomeRoles } from 'services/Authorization';
import { Roles } from 'UI/constants/roles';
import { getTodayDate } from 'UI/utils';

import { getExtraFilters, getSavedYearFilter } from '../commissionsBoard.utils';

const currentYear = getTodayDate().split('-')[0];

export const useCommissionsBoard = () => {
  const [user] = useUserContext();
  const externalEmployeeId = user?.external_employee_id;
  const isOnlyRecruiter = hasSingleRole(Roles.Recruiter);
  const isCoach = userHasRoles([Roles.Coach]);
  const hasSuperiorRole = userHasSomeRoles(user, [
    Roles.Finance,
    Roles.Operations,
    Roles.ProductionDirector,
    Roles.Leadership,
    Roles.DataCoordinator
  ]);

  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState(null);
  const [filters, setFilters] = useState({});

  const selectedYear = useMemo(() => {
    return filters?.years?.value?.title ?? getSavedYearFilter();
  }, [filters]);

  const { state: yearsState } = useFetchWithStatusV2({
    endpoint: `${COMMAND_CENTER_BASE_URL}/${ENDPOINTS.productionYearsAvailable}`,
    apiInstance: SECONDARY_API_CLIENT
  });

  const { state: payPeriodsState } = useFetchWithStatusV2({
    endpoint: `${COMMAND_CENTER_BASE_URL}/${ENDPOINTS.commissionsBoardPeriods}?year=${
      selectedYear ?? currentYear
    }`,
    apiInstance: SECONDARY_API_CLIENT
  });

  const extraFilters = useMemo(
    () =>
      getExtraFilters({
        yearsOptions: yearsState?.results?.years ?? [],
        payPeriodsState,
        currentYear
      }),
    [yearsState, payPeriodsState]
  );

  const formatFilters = useCallback((preFilters = {}) => {
    const { years } = preFilters;
    const hasYearFilter = Boolean(years);

    return {
      ...filtersToParams(preFilters),
      ...(!hasYearFilter && { year: currentYear })
    };
  }, []);

  const queryParams = useMemo(() => {
    const params = queryString.stringify(formatFilters(filters), {
      arrayFormat: 'comma'
    });

    if (isOnlyRecruiter) return `${params}&recruiters=${externalEmployeeId}`;

    if (isCoach && !hasSuperiorRole) return `${params}&teams=${externalEmployeeId}`;

    return params;
  }, [externalEmployeeId, filters, formatFilters, hasSuperiorRole, isCoach, isOnlyRecruiter]);

  const handleFiltersChange = selectedFilters => {
    setSelectedPayPeriodId(null);
    setFilters(prev => (isEqual(prev, selectedFilters) ? prev : selectedFilters));
  };

  const handleClickSheetsRow = useCallback(row => {
    setSelectedPayPeriodId(prev => (row?.payPeriodId === prev ? null : row?.payPeriodId));
  }, []);

  return {
    extraFilters,
    handleClickSheetsRow,
    handleFiltersChange,
    hasSuperiorRole,
    isOnlyRecruiter,
    queryParams,
    selectedPayPeriodId
  };
};
